import { Component, ElementRef, EventEmitter, Input, Output, signal, viewChild } from '@angular/core';

const INVALID_FILENAME_CHARACTERS = /[\\/:*?"<>|]/;

/** Splits "REPORT.PDF" into ["REPORT", ".PDF"]; names without an extension keep an empty suffix. */
export function splitDocumentFilename(filename: string): { baseName: string; extension: string } {
  const trimmed = filename.trim();
  const dotIndex = trimmed.lastIndexOf('.');
  if (dotIndex <= 0 || dotIndex === trimmed.length - 1) {
    return { baseName: trimmed, extension: '' };
  }

  return { baseName: trimmed.slice(0, dotIndex), extension: trimmed.slice(dotIndex) };
}

/**
 * Filename cell for documentation tables with inline rename. Only the base name is editable so the
 * file extension (and with it the document type) never changes. Emits the full new filename.
 */
@Component({
  selector: 'app-document-name-cell',
  template: `
    @if (isEditing()) {
      <div class="document-name-editor">
        <div class="document-name-field">
          <input
            #nameInput
            type="text"
            class="kt-input kt-input-sm"
            [value]="draft()"
            [attr.aria-label]="'New name for ' + filename"
            (input)="onDraftInput($any($event.target).value)"
            (keydown.enter)="$event.preventDefault(); save()"
            (keydown.escape)="cancel()"
          />
          @if (extension()) {
            <span class="document-name-extension">{{ extension() }}</span>
          }
          <button type="button" class="kt-btn kt-btn-sm kt-btn-icon kt-btn-primary" (click)="save()" aria-label="Save name">
            <i class="ki-filled ki-check" aria-hidden="true"></i>
          </button>
          <button type="button" class="kt-btn kt-btn-sm kt-btn-icon kt-btn-outline" (click)="cancel()" aria-label="Cancel rename">
            <i class="ki-filled ki-cross" aria-hidden="true"></i>
          </button>
        </div>
        @if (error()) {
          <span class="document-name-error">{{ error() }}</span>
        }
      </div>
    } @else {
      <div class="document-name-view">
        @if (link) {
          <a [href]="link" target="_blank" rel="noopener noreferrer" class="text-primary hover:underline">{{ filename || '-' }}</a>
        } @else {
          <span>{{ filename || '-' }}</span>
        }
        @if (saving) {
          <span class="document-name-spinner" role="status" aria-label="Renaming"></span>
        } @else {
          <button
            type="button"
            class="document-name-edit"
            [disabled]="disabled"
            (click)="startEditing()"
            [attr.aria-label]="'Rename ' + (filename || 'document')"
            title="Rename"
          >
            <i class="ki-filled ki-pencil" aria-hidden="true"></i>
          </button>
        }
      </div>
    }
  `,
  styles: `
    .document-name-view {
      display: inline-flex;
      align-items: center;
      gap: 0.375rem;
    }

    .document-name-edit {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 1.5rem;
      height: 1.5rem;
      border-radius: 0.375rem;
      color: var(--color-muted-foreground, #9ca3af);
      opacity: 0;
      transition: opacity 0.15s ease, color 0.15s ease, background-color 0.15s ease;
    }

    :host-context(tr:hover) .document-name-edit,
    .document-name-edit:focus-visible {
      opacity: 1;
    }

    .document-name-edit:hover:not(:disabled) {
      color: var(--color-primary, #1b84ff);
      background-color: var(--color-accent, rgba(0, 0, 0, 0.05));
    }

    .document-name-edit:disabled {
      cursor: not-allowed;
    }

    .document-name-editor {
      display: grid;
      gap: 0.25rem;
      min-width: 16rem;
    }

    .document-name-field {
      display: flex;
      align-items: center;
      gap: 0.375rem;
    }

    .document-name-field input {
      flex: 1;
      min-width: 8rem;
    }

    .document-name-extension {
      font-size: 0.8125rem;
      color: var(--color-secondary-foreground, #6b7280);
    }

    .document-name-error {
      font-size: 0.75rem;
      color: var(--color-destructive, #dc2626);
    }

    .document-name-spinner {
      width: 0.875rem;
      height: 0.875rem;
      border: 2px solid var(--color-primary, #1b84ff);
      border-right-color: transparent;
      border-radius: 9999px;
      animation: document-name-spin 0.8s linear infinite;
    }

    @keyframes document-name-spin {
      to {
        transform: rotate(360deg);
      }
    }
  `,
})
export class DocumentNameCell {
  @Input({ required: true }) filename = '';
  @Input() link: string | null | undefined = null;
  @Input() disabled = false;
  @Input() saving = false;
  /** Other filenames in the same table, used to block duplicates. */
  @Input() siblingNames: readonly string[] = [];

  @Output() readonly rename = new EventEmitter<string>();

  private readonly nameInput = viewChild<ElementRef<HTMLInputElement>>('nameInput');

  protected readonly isEditing = signal(false);
  protected readonly draft = signal('');
  protected readonly extension = signal('');
  protected readonly error = signal('');

  protected startEditing(): void {
    if (this.disabled || this.saving) {
      return;
    }

    const { baseName, extension } = splitDocumentFilename(this.filename);
    this.draft.set(baseName);
    this.extension.set(extension);
    this.error.set('');
    this.isEditing.set(true);
    setTimeout(() => {
      const input = this.nameInput()?.nativeElement;
      input?.focus();
      input?.select();
    });
  }

  protected onDraftInput(value: string): void {
    this.draft.set(value);
    this.error.set('');
  }

  protected cancel(): void {
    this.isEditing.set(false);
    this.error.set('');
  }

  protected save(): void {
    const baseName = this.draft().trim();
    if (!baseName) {
      this.error.set('Name is required.');
      return;
    }

    if (INVALID_FILENAME_CHARACTERS.test(baseName)) {
      this.error.set('Name cannot contain \\ / : * ? " < > |');
      return;
    }

    const nextFilename = `${baseName}${this.extension()}`;
    if (nextFilename === this.filename.trim()) {
      this.cancel();
      return;
    }

    const normalized = nextFilename.toUpperCase();
    const isDuplicate = this.siblingNames.some(
      (name) => name !== this.filename && name.trim().toUpperCase() === normalized,
    );
    if (isDuplicate) {
      this.error.set('Another document already uses this name.');
      return;
    }

    this.isEditing.set(false);
    this.rename.emit(nextFilename);
  }
}
